import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Loader from '../common/Loader.js';
import Button from '../common/Button.js';
import authService from '../../services/authService';
import { useTranslation } from 'react-i18next';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const response = await authService.forgotPassword(email);
      setMessage(response.message || 'OTP sent successfully to your email');

      sessionStorage.setItem('resetEmail', email);

      setTimeout(() => {
        navigate('/reset-password');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Show loader when sending OTP
  if (loading) {
    return <Loader fullScreen={true} size="default" message="Sending OTP to your email..." />
  }

  return (
    <div className="min-h-screen md:h-screen relative md:overflow-hidden bg-transparent">

      {/* ===== BACKGROUND IMAGE (tablet & desktop only) ===== */}
      <div
        className="absolute inset-0 bg-cover bg-center z-0"
        style={{
          backgroundImage: "url('/images/login-bg-gradient.png')",
        }}
      />

      {/* ===== CONTENT WRAPPER ===== */}
      <div className="relative z-10 min-h-screen md:h-full flex items-center justify-center md:justify-start py-10 md:py-0">
        <div className="container mx-auto px-4 md:px-6 w-full">

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">

            {/* ================= LEFT CONTENT ================= */}
            <div className="block">

              <h1 className="text-4xl font-bold tracking-tight">
                <img
                  src="/images/DQ-logix-image.png"
                  alt="DQ Logix Logistics" 
                  className="w-[50%]"
                />
              </h1>

              <p className="text-lg uppercase tracking-widest text-gray-500 mt-6">
                From shipment to settlement,
              </p>

              <p className="text-3xl font-semibold text-[#3467ae] uppercase mt-2">
                We support your entire journey.
              </p>

              {/* ILLUSTRATION */}
              <img
                src="/images/dq-logix-login-illustration.png"
                alt="DQ Logix Logistics"
                className="w-full max-w-4xl drop-shadow-xl mt-10 hidden md:block"
              />

              {/* ===== FOOTER NOTE ===== */}
              <p className="text-sm text-gray-400 text-left mt-8 hidden md:block">
                All Rights Reserved. Designed with passion by{" "}
                <span className="font-semibold text-[#3467ae]">DIGIQIN</span>
              </p>
            </div>

            {/* ================= RIGHT FORGOT PASSWORD CARD ================= */}
            <div className="flex justify-center md:justify-end w-full">
              <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 space-y-8">

                {/* ===== FORGOT PASSWORD SECTION ===== */}
                <div>
                  <h2 className="text-xl font-semibold mb-2">
                    Forgot Password
                  </h2>
                  <p className="text-sm text-gray-500 mb-6">
                    Enter your registered email to receive an OTP
                  </p>

                  {/* ERROR MESSAGE */}
                  {error && (
                    <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                      {error}
                    </div>
                  )}

                  {/* SUCCESS MESSAGE */}
                  {message && (
                    <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                      {message}
                      <p className="text-xs mt-1 text-green-600">
                        Redirecting to reset password...
                      </p>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* EMAIL INPUT */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        placeholder="Enter your registered email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 outline-none"
                        required
                        disabled={loading}
                      />
                    </div>

                    {/* SEND OTP BUTTON */}
                    <Button
                      variant="primary"
                      type="submit"
                      fullWidth
                      loading={loading}
                      size="lg"
                      className="py-3 rounded-lg"
                    >
                      {loading ? 'SENDING OTP...' : 'SEND OTP'}
                    </Button>

                    {/* BACK TO LOGIN */}
                    <div className="flex flex-col items-center gap-2 pt-4">
                      <Link
                        to="/login"
                        className="inline-flex items-center justify-center w-full py-2.5 px-4 rounded-lg border border-[#3467ae] text-sm font-medium text-[#3467ae] hover:bg-[#3467ae]/5 transition"
                      >
                        ← Back to Login
                      </Link>
                    </div>

                    <p className="text-xs text-gray-500 text-center pt-2">
                      By continuing, you accept our Terms & Conditions
                      and Privacy Policy.
                    </p>
                  </form>
                </div>

              </div>
            </div>

            <p className="text-sm text-gray-400 text-left mt-0 md:hidden sm:block">
                All Rights Reserved. Designed with passion by{" "}
                <span className="font-semibold text-[#3467ae]">DIGIQIN</span>
            </p>

          </div>

        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;